import {
  getFirstAttributeValue,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'
import type { SkiGogglesSize } from '~/types/pdp'

export const useGlasses = (
  product: MaybeRefOrGetter<Product | null>,
  variantsSortedByPrice: MaybeRefOrGetter<Variant[]> = ref([]),
) => {
  const productColor = computed(() =>
    [
      getFirstAttributeValue(toValue(product)?.attributes, 'frameColor')?.label,
      getFirstAttributeValue(toValue(product)?.attributes, 'lensBaseColor')
        ?.label,
    ]
      .filter((v) => v)
      .join(' / '),
  )

  const frameSizeOptions = computed(() =>
    (toValue(product)?.variants || []).map((variant, key) => {
      return {
        label: getFrameSizesForVariant(variant),
        shortLabel: getFirstAttributeValue(variant.attributes, 'headWidth')
          ?.label, // shown only on PDPs in dropdown size picker together with label
        key,
        variantId: variant.id,
      }
    }),
  )

  const packageUnits = computed(() => {
    // Make sure variantsSortedByPrice and frameSizeOptions are sorted equally
    return toValue(variantsSortedByPrice).map((variant: any, key: number) => {
      const frameSizeOption = frameSizeOptions.value.find(
        (option: { label: string; key: number; variantId: number }) =>
          (option as any).variantId === variant.id,
      )

      return {
        ...frameSizeOption,
        disabled: !isVariantAvailable(product, variant),
        key,
      }
    })
  })

  const isMonoLens = computed(() => {
    const p = toValue(product)
    return p ? isMonoLensGlassProduct(p) : false
  })

  const skiGogglesSize = computed<SkiGogglesSize | null>(() => {
    const variants = toValue(product)?.variants || []
    if (variants.length === 0) return null

    const firstVariant = variants[0]

    // There is a typo in the attribute name, so we have a fallback for the correct one
    const sizeAttribute =
      getFirstAttributeValue(firstVariant.attributes, 'skiGooglesSize') ||
      getFirstAttributeValue(firstVariant.attributes, 'skiGogglesSize')

    if (!sizeAttribute) return null

    return {
      label: sizeAttribute?.label,
      value: sizeAttribute?.value,
    } as SkiGogglesSize
  })

  return {
    productColor,
    frameSizeOptions,
    packageUnits,
    isMonoLens,
    skiGogglesSize,
  }
}
